import { useState } from "react";
import { get } from "lodash";
import { DEFAULT_STATE_PREVIEW_KEY } from "./catalog";
import useScreenWrites from "./useScreenWrites";

/**
 * The propose-then-apply cycle for a screen correction, shared by every surface
 * that offers it. It lives here rather than in a panel because the guarantee it
 * carries is not visual: writing the markup and re-running detection must stay
 * one indivisible step, and a second copy of that pairing is a second chance to
 * get it wrong.
 */
const useScreenRevision = ({
  screenVersionId,
  states,
  defaultLabel,
  revise,
}) => {
  const [proposals, setProposals] = useState([]);
  const [revising, setRevising] = useState(false);
  const [applying, setApplying] = useState(false);

  const { writeVersion, writeState } = useScreenWrites();

  const nameOf = (target) => {
    if (target === DEFAULT_STATE_PREVIEW_KEY) return defaultLabel;
    const state = (states || []).find((item) => item.id === target);
    return get(state, "name") || target;
  };

  /**
   * Targets are handled one by one and failures collected rather than thrown: a
   * state the model mishandles must not cancel the proposals already obtained
   * for the others.
   */
  const propose = async (targets, instructions) => {
    const trimmed = String(instructions || "").trim();
    if (!trimmed || !targets.length || revising) return { failed: [] };
    setRevising(true);
    const produced = [];
    const failed = [];
    try {
      for (const target of targets) {
        const isVersion = target === DEFAULT_STATE_PREVIEW_KEY;
        try {
          const proposal = await revise({
            screenVersion: { id: screenVersionId },
            screenState: isVersion ? undefined : { id: target },
            instructions: trimmed,
          });
          if (proposal) {
            produced.push({
              ...proposal,
              isVersion,
              targetName: isVersion ? defaultLabel : proposal.targetName,
            });
          }
        } catch (error) {
          failed.push(nameOf(target));
        }
      }
      setProposals(produced);
      return { produced, failed };
    } finally {
      setRevising(false);
    }
  };

  /**
   * Writing and re-detecting are one gesture on purpose: between the two a
   * screen holds slots that point at markup which no longer exists, and a
   * separate call would make that state reachable.
   */
  const applyOne = async (proposal) => {
    if (proposal.isVersion) {
      await writeVersion({
        id: screenVersionId,
        htmlContent: proposal.htmlContent,
        styleContent: proposal.styleContent,
      });
      return;
    }
    await writeState({
      id: proposal.targetId,
      htmlContent: proposal.htmlContent,
    });
  };

  // Takes the list to write rather than the whole batch: a board produces a
  // body and its states, and applying only some of them is a legitimate
  // choice — adding states to a version that already works, for instance.
  const apply = async (list) => {
    const retained = list || proposals;
    if (applying || !retained.length) return { failed: [] };
    setApplying(true);
    const failed = [];
    const unwritten = [];
    try {
      for (const proposal of retained) {
        try {
          await applyOne(proposal);
        } catch (error) {
          unwritten.push(proposal);
          failed.push(proposal.targetName || defaultLabel);
        }
      }

      // Only what was actually written leaves the list. Clearing everything
      // meant a failure halfway through destroyed the remaining proposals:
      // the markup already applied stayed, the rest was unrecoverable, and
      // the operator had no way to retry short of asking the model again.
      // Proposals left out of this batch survive too — applying a subset is
      // a supported choice, not a discard.
      setProposals((current) =>
        current.filter(
          (item) => retained.indexOf(item) < 0 || unwritten.indexOf(item) >= 0,
        ),
      );
      return { failed };
    } finally {
      setApplying(false);
    }
  };

  const discard = () => setProposals([]);

  return { proposals, revising, applying, propose, apply, discard };
};

export default useScreenRevision;
